<template>
    <div v-click-outside="close" class="filter-select">
        <!-- Trigger Button -->
        <button
            class="filter-select__btn"
            :class="{ 'filter-select__btn--active': selected.size > 0 }"
            @click="toggle"
        >
            <div class="filter-select__btn-label">
                {{ buttonLabel }}
            </div>

            <mdi-icon class="shrink-0 w-5 h-5" icon="mdiMenuDown" />
        </button>

        <!-- Drop-Down Panel -->
        <div v-if="isOpen" class="filter-select__panel">
            <!-- Header -->
            <div class="filter-select__header">
                <div class="flex justify-between items-center mb-1 w-full">
                    <div class="filter-select__title">
                        {{ label }}
                    </div>

                    <mdi-icon
                        class="filter-select__close"
                        icon="mdiClose"
                        @click="close"
                    />
                </div>

                <!-- Toolbar (checkbox mode only) -->
                <div v-if="!radio" class="filter-select__toolbar">
                    <div class="filter-select__actions">
                        <button @click.stop="selectAll">
                            <!-- @i18n -->
                            Select all

                            <template v-if="hasSearch">
                                ({{ visibleOptionsCount }})
                            </template>
                        </button>

                        <button v-if="selected.size > 0" @click.stop="reset">
                            <!-- @i18n -->
                            Reset
                        </button>
                    </div>

                    <mdi-icon
                        v-if="hasSearch"
                        icon="mdiMagnify"
                        class="filter-select__search-toggle w-4 h-4"
                        :class="{ 'filter-select__search-toggle--active': searchOpen }"
                        @click.stop="toggleSearch"
                    />
                </div>

                <!-- Search Box -->
                <div v-if="!radio && searchOpen" class="relative">
                    <input
                        ref="searchBox"
                        v-model="searchText"
                        type="text"
                        class="filter-select__search-box"
                        :placeholder="t('common.search_ellipsis')"
                    />

                    <mdi-icon
                        v-if="searchText != ''"
                        icon="mdiClose"
                        class="filter-select__search-clear"
                        @click.stop="searchText = ''"
                    />
                </div>
            </div>

            <!-- Options List -->
            <div class="filter-select__options">
                <!-- "Any X" sentinel for radio mode -->
                <div
                    v-if="radio && !hasNull"
                    class="filter-select__item"
                    :class="{ 'filter-select__item--selected': selected.size === 0 }"
                    @click.stop="reset"
                >
                    <div class="filter-select__radio"></div>

                    <div class="filter-select__item-label">
                        <!-- @i18n -->
                        Any Option
                    </div>
                </div>

                <template v-for="(items, group) in visibleOptions" :key="group">
                    <div v-if="group !== ''" class="filter-select__group-heading">
                        {{ group }}
                    </div>

                    <div
                        v-for="item in items"
                        :key="item.value"
                        class="filter-select__item"
                        :class="{
                            'filter-select__item--selected': isSelected(item.value),
                            'filter-select__item--disabled': item.disabled,
                        }"
                        @click.stop="item_onClick(item)"
                    >
                        <div v-if="radio" class="filter-select__radio"></div>

                        <div
                            v-else-if="!isSelected(item.value)"
                            class="filter-select__checkbox"
                        >
                        </div>

                        <mdi-icon
                            v-else
                            icon="mdiCheck"
                            class="filter-select__checkbox"
                        />

                        <div class="filter-select__item-label">
                            {{ item.label }}
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * @typedef {{
 *     label:     string,
 *     value:     string,
 *     disabled?: boolean,
 * }} OptionItem
 */

/**
 * @prop {string|number|boolean|Array|Object|null} modelValue
 *   The current selection. In checkbox mode, an array of selected values;
 *   in radio mode, a single value or null when nothing is selected.
 *
 * @prop {Record<string, OptionItem[]>} options
 *   Grouped options. Each key is a group name (use an empty string for
 *   ungrouped items) and its value is the list of options in that group.
 *
 * @prop {string} label
 *   Display label shown in the trigger button and the panel header.
 *
 * @prop {boolean} [radio=false]
 *   When true, renders a single-select (radio) list and closes the panel
 *   after each selection. When false (default), renders a multi-select
 *   (checkbox) list with Select-all / Reset actions and an optional search.
 *
 * @type {{
 *     modelValue: string|number|boolean|Array|Object|null,
 *     options:    Record<string, OptionItem[]>,
 *     label:      string,
 *     radio:      boolean,
 * }}
 */
const props = defineProps({
    modelValue:
    {
        type:    [String, Number, Boolean, Array, Object, null],
        default: null,
    },

    options:
    {
        type:     Object,
        required: true,
    },

    label:
    {
        type:     String,
        required: true,
    },

    radio:
    {
        type:    Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n({ useScope: 'global' });

const searchBox = useTemplateRef('searchBox');

const isOpen     = ref(false);
const searchText = ref('');
const searchOpen = ref(false);

const selected = computed(() =>
{
    if(props.radio)
    {
        if(props.modelValue === null)
        {
            return new Set();
        }
        return new Set([props.modelValue]);
    }

    return new Set(props.modelValue ?? []);
});

const flatOptions = computed(() =>
{
    const out = [];
    for(const key in props.options)
    {
        out.push(...props.options[key]);
    }
    return out;
});

const hasSearch = computed(() => flatOptions.value.length >= 5);
const hasNull = computed(() => flatOptions.value.some(o => o.value === null));

const visibleOptions = computed(() =>
{
    if(!searchText.value)
    {
        return props.options;
    }

    const q = searchText.value.toLowerCase();
    const result = {};
    for(const key in props.options)
    {
        const matches = props.options[key].filter(
            item => item.label.toLowerCase().includes(q)
        );
        if(matches.length)
        {
            result[key] = matches;
        }
    }
    return result;
});

const flatVisibleOptions = computed(() =>
{
    const out = [];
    for(const key in visibleOptions.value)
    {
        out.push(...visibleOptions.value[key]);
    }
    return out;
});

const visibleOptionsCount = computed(() => flatVisibleOptions.value.length);

const buttonLabel = computed(() =>
{
    let label = props.label;
    if(selected.value.size)
    {
        label += ': ';
    }
    label += Array.from(selected.value).map(v => getOptionLabel(v)).join(', ');
    return label;
});

function getOptionLabel(value)
{
    return flatOptions.value.find(o => o.value === value)?.label ?? value;
}

/**
 * Click handler for options.
 *
 * @async
 * @param {OptionItem} item
 * @returns {Promise<void>}
 */
async function item_onClick(item)
{
    if(item.disabled)
    {
        return;
    }

    if(props.radio)
    {
        if(isSelected(item.value))
        {
            // Radio item already selected.
            // Toggle it off.
            emit('update:modelValue', null);
        }
        else
        {
            // Radio item not selected.
            // Select it.
            emit('update:modelValue', item.value);
        }

        await nextTick();
        close();
        return;
    }

    // Toggle checkbox item
    const sel = selected.value;
    if(isSelected(item.value))
    {
        sel.delete(item.value);
    }
    else
    {
        sel.add(item.value);
    }
    emit('update:modelValue', Array.from(sel));
}

function isSelected(value)
{
    if(value === null)
    {
        return selected.value.size === 0;
    }

    return selected.value.has(value);
}

function toggle()
{
    if(isOpen.value)
    {
        close();
    }
    else
    {
        isOpen.value = true;
    }
}

async function toggleSearch()
{
    searchOpen.value = !searchOpen.value;
    await nextTick();

    if(searchOpen.value)
    {
        // Focus the search box when opening it
        searchBox.value?.focus();
    }
    else
    {
        // Clear the search box when closing it
        searchText.value = '';
    }
}

function close()
{
    isOpen.value = false;
    if(searchOpen.value && searchText.value?.trim() === '')
    {
        searchOpen.value = false;
        searchText.value = '';
    }
}

function selectAll()
{
    const sel = new Set();
    flatVisibleOptions.value.forEach(o =>
    {
        if(!o.disabled)
        {
            sel.add(o.value);
        }
    });
    emit('update:modelValue', Array.from(sel));
}

function reset()
{
    if(props.radio)
    {
        emit('update:modelValue', null);
    }
    else
    {
        emit('update:modelValue', []);
    }

    close();
}
</script>

<style scoped>
@reference "#main.css";

.filter-select
{
    @apply relative text-sm text-gray-600 select-none;
}

/* Trigger button */
.filter-select__btn
{
    @apply flex justify-between items-center gap-2
        rounded border border-gray-300 px-2 py-1 w-full
        bg-white text-gray-700 text-left
        hover:bg-gray-50;
}

.filter-select__btn--active
{
    @apply border-primary-500 bg-primary-50 text-primary-700
        hover:bg-primary-100;
}

.filter-select__btn-label
{
    @apply grow truncate;
}

.filter-select__btn--active .filter-select__btn-label
{
    @apply text-primary-700;
}

/* Panel */
.filter-select__panel
{
    @apply absolute top-full -left-2 z-10 mt-1 overflow-hidden
        rounded-lg bg-white shadow-lg;

    min-width: 210px;
    width: calc(100% + 2 * theme('size.2'));
}

/* Panel header */
.filter-select__header
{
    @apply border-b border-gray-200 p-3;
}

.filter-select__title
{
    @apply text-base text-gray-800 font-bold truncate;
}

.filter-select__close
{
    @apply cursor-pointer shrink-0 w-4 h-4 text-gray-500
        hover:text-primary-500;
}

/* Toolbar (checkbox mode) */
.filter-select__toolbar
{
    @apply flex justify-between items-center;
}

.filter-select__actions
{
    @apply flex gap-3 text-primary-500;

    & > button:hover
    {
        @apply underline;
    }
}

.filter-select__search-toggle
{
    @apply cursor-pointer transition-colors hover:text-primary-500;
}

.filter-select__search-toggle--active
{
    @apply text-primary-500;
}

/* Search box */
.filter-select__search-box
{
    @apply block mt-1 rounded border border-gray-300 px-2 py-1 w-full
        outline-none pr-6
        focus:border-primary-500
        placeholder:text-gray-400;
}

.filter-select__search-clear
{
    @apply cursor-pointer absolute right-2 top-1/2 -translate-y-1/2
        w-3.5 h-3.5 text-gray-500 transition
        hover:text-primary-500;
}

/* Options list */
.filter-select__options
{
    @apply max-h-[240px] overflow-y-auto;
}

.filter-select__group-heading
{
    @apply p-3 pb-1 text-gray-800 font-bold;
}

/* Option item */
.filter-select__item
{
    @apply cursor-pointer flex items-center gap-2 px-3 py-1 text-gray-700;

    &:hover
    {
        @apply bg-gray-50;
    }
}

.filter-select__item--disabled
{
    @apply cursor-not-allowed opacity-50;
}

.filter-select__item-label
{
    @apply truncate;
}

.filter-select__radio
{
    @apply flex justify-center items-center
        rounded-full border border-gray-500 p-0.5 w-4 h-4;

    &::after
    {
        content: "";

        @apply rounded-full w-full h-full bg-transparent;
    }
}

.filter-select__item--selected .filter-select__radio::after
{
    @apply bg-primary-500;
}

.filter-select__checkbox
{
    @apply shrink-0 w-4 h-4 text-gray-500;

    &:not(.mdi-icon)
    {
        @apply rounded border border-gray-500;
    }
}

.filter-select__item--selected .filter-select__checkbox
{
    @apply text-primary-500;
}
</style>
